import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  MEDIOS_PAGO_LABEL,
  calcularArqueo,
  closeCaja,
  getCajaAbierta,
  listCajas,
  openCaja,
} from '../services/cashService';
import { formatDateTime, formatMoney } from '../lib/format';
import { friendlyError } from '../lib/errors';
import DataTable from '../components/ui/DataTable';
import LoadingState from '../components/ui/LoadingState';
import ErrorState from '../components/ui/ErrorState';
import EmptyState from '../components/ui/EmptyState';
import Badge from '../components/ui/Badge';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import StatCard from '../components/ui/StatCard';

export default function CashPage() {
  const { ruc, firebaseUser, user } = useAuth();
  const navigate = useNavigate();
  const [cajas, setCajas] = useState([]);
  const [abierta, setAbierta] = useState(null);
  const [arqueo, setArqueo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [closeModal, setCloseModal] = useState(false);
  const [monto, setMonto] = useState('');
  const [busy, setBusy] = useState(false);

  const canManage = user?.rol !== 'prueba';

  const load = useCallback(async () => {
    if (!ruc) return;
    setLoading(true);
    setError(null);
    try {
      const [list, open] = await Promise.all([listCajas({ ruc, pageSize: 10 }), getCajaAbierta({ ruc })]);
      setCajas(list);
      setAbierta(open);
      if (open) {
        setArqueo(await calcularArqueo({ ruc, cajaId: open.id }));
      } else {
        setArqueo(null);
      }
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setLoading(false);
    }
  }, [ruc]);

  useEffect(() => {
    load();
  }, [load]);

  const onOpen = async () => {
    setBusy(true);
    setError(null);
    try {
      await openCaja({
        ruc,
        montoInicial: monto,
        cajeroId: firebaseUser?.uid || user?.id || '',
        cajeroName: [user?.name, user?.apellidos].filter(Boolean).join(' ') || user?.email || 'Admin web',
      });
      setOpenModal(false);
      setMonto('');
      await load();
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setBusy(false);
    }
  };

  const onClose = async () => {
    if (!abierta) return;
    setBusy(true);
    setError(null);
    try {
      await closeCaja({ ruc, cajaId: abierta.id, montoFinal: monto });
      setCloseModal(false);
      setMonto('');
      await load();
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setBusy(false);
    }
  };

  const columns = [
    { key: 'estado', label: 'Estado', render: (r) => <Badge tone={r.estado === 'abierta' ? 'ok' : 'muted'}>{r.estado}</Badge> },
    { key: 'cajeroName', label: 'Cajero', render: (r) => r.cajeroName || r.cajeroId || '—' },
    { key: 'fechaApertura', label: 'Apertura', render: (r) => formatDateTime(r.fechaApertura) },
    { key: 'fechaCierre', label: 'Cierre', render: (r) => formatDateTime(r.fechaCierre) },
    { key: 'montoInicial', label: 'Inicial', render: (r) => formatMoney(r.montoInicial) },
    { key: 'montoFinal', label: 'Final', render: (r) => formatMoney(r.montoFinal) },
    {
      key: 'actions',
      label: 'Acciones',
      render: (r) => (
        <button
          type="button"
          className="btn btn-mini"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/admin/caja/${r.id}`);
          }}
        >
          Ver
        </button>
      ),
    },
  ];

  if (loading) return <LoadingState label="Cargando caja…" />;

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1>Caja</h1>
          <p className="muted">
            Colección <code>cajas</code> · arqueo desde pedidos pagados de la caja · sin contabilidad paralela
          </p>
        </div>
        {canManage ? (
          abierta ? (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                setMonto('');
                setCloseModal(true);
              }}
            >
              Cerrar caja
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                setMonto('');
                setOpenModal(true);
              }}
            >
              Abrir caja
            </button>
          )
        ) : null}
      </header>

      {error ? <div className="alert error">{error}</div> : null}

      <section className="stat-grid">
        <StatCard
          label="Caja actual"
          value={abierta ? 'Abierta' : 'Cerrada'}
          tone={abierta ? 'primary' : 'default'}
          hint={abierta?.cajeroName}
        />
        <StatCard label="Monto inicial" value={formatMoney(abierta?.montoInicial ?? 0)} />
        <StatCard label="Ventas (arqueo)" value={formatMoney(arqueo?.totalVentas ?? 0)} hint={`${arqueo?.totalDocumentos ?? 0} pedidos pagados`} />
        <StatCard label="Efectivo" value={formatMoney(arqueo?.ventasEfectivo ?? 0)} />
        <StatCard label="Saldo esperado" value={formatMoney((Number(abierta?.montoInicial) || 0) + (arqueo?.ventasEfectivo || 0))} />
      </section>

      {abierta && arqueo ? (
        <section className="panel">
          <header className="panel-header">
            <h2>Desglose por medio de pago</h2>
          </header>
          <ul className="simple-list">
            {Object.entries(arqueo.ventasPorMedio).map(([medio, total]) => (
              <li key={medio}>
                <span>{MEDIOS_PAGO_LABEL[medio] || medio}</span>
                <strong>{formatMoney(total)}</strong>
              </li>
            ))}
            {Object.keys(arqueo.ventasPorMedio).length === 0 ? (
              <li className="muted">Sin ventas en esta caja todavía.</li>
            ) : null}
          </ul>
        </section>
      ) : null}

      <section className="panel">
        <header className="panel-header">
          <h2>Historial de cajas</h2>
          <span className="muted">últimas 10</span>
        </header>
        <DataTable
          columns={columns}
          rows={cajas}
          keyField="id"
          onRowClick={(row) => navigate(`/admin/caja/${row.id}`)}
          empty={<EmptyState title="Sin cajas registradas" />}
        />
      </section>

      <ConfirmDialog
        open={openModal}
        title="Abrir caja"
        message={
          <span>
            <label className="dialog-field">
              Monto inicial (S/)
              <input type="number" min="0.01" step="0.01" value={monto} onChange={(e) => setMonto(e.target.value)} autoFocus />
            </label>
          </span>
        }
        confirmLabel="Abrir"
        onConfirm={onOpen}
        onCancel={() => setOpenModal(false)}
        loading={busy}
      />

      <ConfirmDialog
        open={closeModal}
        title="Cerrar caja"
        message={
          <span>
            Efectivo esperado:{' '}
            <strong>{formatMoney((Number(abierta?.montoInicial) || 0) + (arqueo?.ventasEfectivo || 0))}</strong>
            <label className="dialog-field">
              Conteo final (S/)
              <input type="number" min="0" step="0.01" value={monto} onChange={(e) => setMonto(e.target.value)} autoFocus />
            </label>
          </span>
        }
        confirmLabel="Cerrar"
        onConfirm={onClose}
        onCancel={() => setCloseModal(false)}
        loading={busy}
      />
    </div>
  );
}
